import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { err, type ParsedList, type Result, type VideoSummary } from '@visious/core';
import { fetchHashtag } from '../../api/search';
import type { ApiProblem } from '../../api/videos';
import { useWatchProgressLookup } from '../../history/useWatchProgress';
import { systemClock } from '../../platform/clock';
import { getPlatform } from '../../platform/instance';
import { hashtagStateFor, type HashtagState } from './model';

/**
 * The React binding for `/hashtag/TAG`. Every decision is in `model.ts`; what is here is the query and
 * the clock.
 *
 * It is its own file rather than living beside the JSX — which is where it was first written, on the
 * grounds that the screen is small. Dependency-cruiser refused that, correctly: `err`, `ok` and three
 * types come from core, and non-negotiable 2 says a `.tsx` does not import core at all. The rule is
 * about the *edge*, not about the line count, and the guardrail catching a shortcut on the day it was
 * taken is exactly what it is for.
 *
 * `useInfiniteQuery` since auto-load arrived, search's shape: pages append under one URL, the route's
 * `page` is where the list starts, and the end is proved by an empty page (`model.ts`).
 */
export interface HashtagBinding {
  readonly state: HashtagState;
  readonly retry: () => void;
  readonly loadMore: () => void;
  readonly loadingMore: boolean;
}

export function useHashtag(tag: string, page: number): HashtagBinding {
  const request = useInfiniteQuery({
    queryKey: ['hashtag', tag, page],
    initialPageParam: page,
    queryFn: async ({ pageParam }): Promise<HashtagResult> => {
      const boot = await getPlatform();
      if (!boot.ok) return err({ kind: 'bad-url', path: `/api/v1/hashtag/${tag}` });
      return await fetchHashtag(boot.platform, tag, pageParam);
    },
    getNextPageParam: (_last, all) => page + all.length,
    // §3's retry and backoff live in `createApiTransport` — a second loop here
    // would multiply requests against an instance that is already failing (1c).
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const progress = useWatchProgressLookup();

  const state = useMemo(() => {
    if (request.isPending) return hashtagStateFor(tag, page, null, { nowEpochSec: 0, progress });

    const results: readonly HashtagResult[] =
      request.error !== null
        ? // A thrown query is a bug or an abort rather than a classified failure — the transport returns
          // its errors as values. Reported as a network problem, the honest reading of "no answer".
          [err({ kind: 'transport', error: { kind: 'network', status: null, url: '', retryable: true } })]
        : (request.data?.pages ?? []);

    // Anchored to when the rows arrived, not to this render (`useFeed.ts` explains why).
    const arrivedAt = request.dataUpdatedAt > 0 ? request.dataUpdatedAt : systemClock.nowEpochMs();
    return hashtagStateFor(tag, page, results, { nowEpochSec: Math.floor(arrivedAt / 1000), progress });
  }, [page, progress, request.data, request.dataUpdatedAt, request.error, request.isPending, tag]);

  return {
    state,
    retry: () => void request.refetch(),
    loadMore: () => {
      if (state.kind === 'ready' && state.more && !request.isFetchingNextPage) {
        void request.fetchNextPage();
      }
    },
    loadingMore: request.isFetchingNextPage,
  };
}

type HashtagResult = Result<ParsedList<VideoSummary>, ApiProblem>;
